export type Takeover = {
  schemaVersion: 1;
  enabled: true;
  id: string;
  startDate?: string | null;
  endDate?: string | null;
  title?: string | null;
  message?: string | null;
  ctaText?: string | null;
  ctaUrl?: string | null;
  imageUrl?: string | null;
};

const TAKEOVER_URL = "https://kellydornhaus.com/takeover.json";

function ymd(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function normalizeSlug(value: string): string {
  return value.trim().toLowerCase();
}

function parseSlugList(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;

  const items: string[] = [];
  for (const v of value) {
    if (typeof v !== "string") continue;
    const slug = normalizeSlug(v);
    if (slug) items.push(slug);
  }
  return items;
}

// Cleared for good — the player opened the announcement and read it.
export function getTakeoverDismissKey(siteId: string, takeoverId: string): string {
  return `kd_takeover_dismissed:${normalizeSlug(siteId)}:${takeoverId}`;
}

export async function maybeFetchTakeover(siteId: string): Promise<Takeover | null> {
  if (typeof window === "undefined") return null;

  try {
    const res = await fetch(`${TAKEOVER_URL}?d=${Date.now()}`);
    if (!res.ok) return null;

    const data: unknown = await res.json();
    if (!data || typeof data !== "object") return null;
    const t = data as Record<string, unknown>;

    if (t.schemaVersion !== 1 || t.enabled !== true) return null;

    const id = typeof t.id === "string" ? t.id.trim() : "";
    if (!id) return null;

    const slug = normalizeSlug(siteId);

    const allowFrom = parseSlugList(t.allowFrom);
    if (allowFrom) {
      if (allowFrom.length === 0) return null;
      if (!allowFrom.includes("*") && !allowFrom.includes(slug)) return null;
    }

    const denyFrom = parseSlugList(t.denyFrom);
    if (denyFrom && denyFrom.includes(slug)) return null;

    const today = ymd(new Date());
    const startDate = typeof t.startDate === "string" ? t.startDate : null;
    const endDate = typeof t.endDate === "string" ? t.endDate : null;
    if (startDate && today < startDate) return null;
    if (endDate && today > endDate) return null;

    try {
      if (window.localStorage.getItem(getTakeoverDismissKey(siteId, id))) return null;
    } catch {
      // ignore storage failures
    }

    return {
      schemaVersion: 1,
      enabled: true,
      id,
      startDate,
      endDate,
      title: typeof t.title === "string" ? t.title : null,
      message: typeof t.message === "string" ? t.message : null,
      ctaText: typeof t.ctaText === "string" ? t.ctaText : null,
      ctaUrl: typeof t.ctaUrl === "string" ? t.ctaUrl : null,
      imageUrl: typeof t.imageUrl === "string" ? t.imageUrl : null,
    };
  } catch {
    return null;
  }
}
